/**
 * Application Insights telemetry client for the Vue 3 SPA.
 *
 * Faithful port of the Vue 2 shell's `Scripts/vue/common/appInsights.js`, adapted
 * to the SPA where the SDK is loaded from the bundle (the Vue 2 shell loaded it via
 * a Razor snippet). Two hard rules keep this safe:
 *
 *  1. **The SDK is dynamically imported inside `initTelemetry`** so it stays out of
 *     the static module graph pulled in by `error.interceptor.ts` → `http-client.ts`
 *     (imported by nearly every service and test). Only a type-only import survives
 *     compilation, which is erased at build time.
 *  2. **Telemetry never throws.** Every public entry point is try/catch-guarded and
 *     silently no-ops when the SDK is uninitialised (empty connection string in
 *     dev/tests, or before `loadAppInsights()` completes).
 */
import type { ApplicationInsights, IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web'
import { TELEMETRY_CONFIG } from '@/config/telemetry.config'

// Application Insights numeric SeverityLevel values (match the SDK enum):
// 2 = Warning, 3 = Error. Kept local so we never import the SDK enum as a value.
const SEVERITY_ERROR = 3

// Module-level singleton. Null until `initTelemetry` completes with a real
// connection string; `trackException` no-ops while it is null.
let appInsights: ApplicationInsights | null = null

/**
 * Initialise the App Insights SDK. No-op when the connection string is empty
 * (dev/tests) or when already initialised. The connection string is an injectable
 * parameter so tests can force the initialised branch without env stubbing.
 *
 * The SDK is auto-instrumentation-free by design: exception tracking is disabled so
 * all exceptions flow through our own handler layers, and ajax/fetch/auto-route
 * tracking is disabled so we never ship free-text search query strings (routed
 * through `?q=`/`GetSuggestions?input=`) to Azure — the product is GDPR-scoped.
 */
export async function initTelemetry(
  connectionString: string = TELEMETRY_CONFIG.connectionString,
): Promise<void> {
  if (!connectionString || appInsights) {
    return
  }
  try {
    const { ApplicationInsights: AppInsightsSdk } = await import('@microsoft/applicationinsights-web')
    const instance = new AppInsightsSdk({
      config: {
        connectionString,
        disableExceptionTracking: true,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        enableAutoRouteTracking: false,
      },
    })
    instance.loadAppInsights()
    appInsights = instance
  } catch (e) {
    // Telemetry must never break app bootstrap.
    if (typeof console !== 'undefined') {
      console.error('[app-insights] Failed to initialise telemetry:', e)
    }
  }
}

/**
 * Forward a frontend exception to Application Insights. Silent no-op when the SDK
 * is uninitialised. Non-Error values are wrapped so the SDK always receives a real
 * Error with a stack. Extra properties are coerced to strings and tagged with
 * `handledAt: 'frontend'` for filtering in Azure.
 *
 * @param error         The thrown value (Error or anything else).
 * @param properties    Extra custom dimensions (source, component, info, ...).
 * @param severityLevel AI SeverityLevel (Warning = 2, Error = 3). Defaults to Error.
 */
export function trackException(
  error: unknown,
  properties?: Record<string, unknown>,
  severityLevel?: number,
): void {
  try {
    if (!appInsights) {
      // SDK not initialised (dev/tests, or before load). Fail silently.
      return
    }

    // The SDK requires a real Error instance to extract a stack trace.
    const exception = error instanceof Error ? error : new Error(stringifyReason(error))
    const severity = typeof severityLevel === 'number' ? severityLevel : SEVERITY_ERROR

    const telemetry: IExceptionTelemetry = {
      exception,
      severityLevel: severity as SeverityLevel,
      properties: sanitizeProperties(properties),
    }
    appInsights.trackException(telemetry)
  } catch (e) {
    // Never let telemetry reporting surface its own error to the user.
    if (typeof console !== 'undefined') {
      console.error('[app-insights] Failed to track exception:', e)
    }
  }
}

// App Insights customDimensions must be string values. Coerce everything, drop
// null/undefined, and always inject `handledAt: 'frontend'`.
function sanitizeProperties(properties?: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = { handledAt: 'frontend' }
  if (properties) {
    for (const key of Object.keys(properties)) {
      const value = properties[key]
      if (value === null || value === undefined) {
        continue
      }
      result[key] = typeof value === 'string' ? value : String(value)
    }
  }
  return result
}

// Best-effort readable message for non-Error rejection reasons (strings, objects).
function stringifyReason(reason: unknown): string {
  if (reason === null || reason === undefined) {
    return 'Unknown error (no reason provided)'
  }
  if (typeof reason === 'string') {
    return reason
  }
  if (typeof reason === 'object' && 'message' in reason) {
    const message = (reason as { message: unknown }).message
    if (typeof message === 'string') {
      return message
    }
  }
  try {
    return JSON.stringify(reason)
  } catch {
    return String(reason)
  }
}
